import { db } from '@/data/firebase/config';
import { useIsSuperAdmin, useRBACContext } from '@/domain/context/rbac';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { User } from '@/domain/types/user';
import AlertMessage from '@/presentation/components/alert-message';
import Button from '@/presentation/components/button';
import Table, { Column } from '@/presentation/components/table';
import {
  collection,
  doc,
  getDocs,
  or,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const UsersSelfRegisteredPage = () => {
  const { loading: rbacLoading } = useRBACContext();
  const isSuperAdmin = useIsSuperAdmin();

  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, DOCUMENTS_DB.USERS),
          where('registeredViaApp', '==', true),
        ),
      );
      setUsers(
        snapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        })) as User[],
      );
    } catch (error) {
      console.error('Error al cargar los usuarios auto-registrados:', error);
      toast.error('No se pudieron cargar los usuarios auto-registrados', {
        theme: 'colored',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleReactivateConfirmed = async () => {
    if (!userToReactivate) return;
    const user = userToReactivate;
    setUserToReactivate(null);
    setIsSaving(true);
    try {
      const strippedDocument = user.document.replace(/-$/, '');
      const duplicatesSnapshot = await getDocs(
        query(
          collection(db, DOCUMENTS_DB.USERS),
          or(
            where('document', '==', strippedDocument),
            where('document', '==', parseInt(strippedDocument, 10)),
          ),
        ),
      );
      const hasActiveDuplicate = duplicatesSnapshot.docs.some(
        (docSnap) =>
          docSnap.id !== user.id && docSnap.data().isEnabled !== false,
      );
      if (hasActiveDuplicate) {
        toast.error(
          'No se pudo reactivar: ya existe otra cuenta activa con el mismo documento. Resuelve manualmente cuál debe quedar activa antes de reactivar esta.',
          { theme: 'colored' },
        );
        return;
      }

      await updateDoc(doc(db, DOCUMENTS_DB.USERS, user.id), {
        isEnabled: true,
        document: strippedDocument,
      });
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success('Usuario reactivado', { theme: 'colored' });
    } catch (error) {
      console.error('Error al reactivar el usuario:', error);
      toast.error('No se pudo reactivar el usuario', { theme: 'colored' });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      [user.document, user.name, user.lastname, user.email].some((field) =>
        String(field ?? '').toLowerCase().includes(term),
      ),
    );
  }, [users, searchTerm]);

  const columns: Column<User>[] = [
    { header: 'Documento', accessor: 'document' },
    { header: 'Nombre', accessor: 'name' },
    { header: 'Apellido', accessor: 'lastname' },
    { header: 'Email', accessor: 'email' },
    {
      header: 'Estado',
      accessor: 'isEnabled',
      render: (value) =>
        value ? (
          <span className="text-green-600 font-semibold">Activo</span>
        ) : (
          <span className="text-red-600 font-semibold">Inactivo</span>
        ),
    },
    {
      header: 'Acciones',
      accessor: 'id',
      render: (_, row) => (
        <Button
          label="Reactivar"
          onClick={() => setUserToReactivate(row)}
          disabled={isSaving}
        />
      ),
    },
  ];

  if (rbacLoading || isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <span>Cargando...</span>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-gray-500">No tienes acceso a esta sección.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="text-2xl font-bold">
        Usuarios auto-registrados (app móvil)
      </h1>
      <input
        type="text"
        className="border border-gray-300 bg-white rounded-md p-2 text-sm"
        onChange={(e) => setSearchTerm(e.target.value)}
        value={searchTerm}
        placeholder="Buscar por documento, nombre, apellido o email"
      />
      {filteredUsers.length === 0 ? (
        <p className="text-gray-500">No se encontraron usuarios.</p>
      ) : (
        <Table data={filteredUsers} columns={columns} />
      )}
      <AlertMessage
        isOpen={!!userToReactivate}
        title="Reactivar usuario"
        message={`¿Seguro que quieres reactivar a ${userToReactivate?.name ?? ''} ${userToReactivate?.lastname ?? ''}? Podrá volver a iniciar sesión con su documento original.`}
        onConfirm={handleReactivateConfirmed}
        onCancel={() => setUserToReactivate(null)}
      />
    </div>
  );
};

export default UsersSelfRegisteredPage;
